import { Directive, effect, ElementRef, OnDestroy, Renderer2 } from '@angular/core';
import { Core } from 'cytoscape';
import { MapService } from '../../api/services/map.service';
import { GraphMap } from '../../api/interfaces/graph-node.interface';
import { SettingsService } from '../../api/services/setting.service';
import { CytoscapeService } from '../../services/cy-services/cytoscape.service';
import { MapRoleService } from '../../acl/map-role.service';
import { transformColorWithHSL } from '../helpers/color-helpers';

@Directive({
  selector: '[appSmapStyles]',
  standalone: true
})
export class SmapStylesDirective implements OnDestroy {
  private readonly DEFAULT_COLOR = '#FFFFFF'; // Default fallback color

  /** Hardcoded magnitude M — controls how far the size slider scales the image. */
  private readonly IMAGE_SIZE_MAGNITUDE = 4;

  // Background-image layer state. The image layer is translated and scaled with
  // the cytoscape viewport (pan/zoom) so it stays fixed in relation to the
  // canvas contents rather than the window.
  private imageWrapper: HTMLElement | null = null;
  private imageBox: HTMLElement | null = null;
  private currentNatural: { w: number; h: number } | null = null;
  private currentSizeValue = 0;
  private naturalSizeByUrl = new Map<string, { w: number; h: number }>();
  private boundCy: Core | null = null;
  private viewportHandler: (() => void) | null = null;
  private rafId: number | null = null;
  private centerPersistedForMapId: string | null = null;

  constructor(private el: ElementRef,
              private mapService: MapService,
              private renderer: Renderer2,
              private cyService: CytoscapeService,
              private mapRoleService: MapRoleService,
              private settingsService: SettingsService) {
    // React to map style changes and appearance (dark/light) changes.
    effect(() => {
      const map = this.mapService.getSelectedMapSignal();
      // Track system dark mode so 'system' appearance re-renders on OS change.
      this.settingsService.systemDarkModeSignal();
      this.applyStyles(map?.styles || {});
    });
  }

  ngOnDestroy(): void {
    this.detachViewportTracking();
  }

  /** Main method to apply all styles */
  private applyStyles(styles: Partial<GraphMap['styles']>): void {
    this.ensureContainerIsSetup();
    this.clearExistingLayers();
    this.createOverlayLayer(styles.color, styles.colorIntensity);
    this.createBackgroundImageLayer(
      styles.backgroundImageUrl,
      styles.opacity,
      styles.backgroundImageSize ?? 0
    );
    this.updateBackgroundColorVariable(styles.color, styles.colorIntensity);
  }

  /** Ensures the container has the correct CSS setup */
  private ensureContainerIsSetup(): void {
    const container = this.el.nativeElement;
    this.renderer.setStyle(container, 'position', 'relative');
    this.renderer.setStyle(container, 'overflow', 'hidden');
  }

  /** Removes any existing layers */
  private clearExistingLayers(): void {
    const container = this.el.nativeElement;
    const existingLayers = container.querySelectorAll('.background-layer, .overlay-layer');
    existingLayers.forEach((layer: HTMLElement) => layer.remove());
  }

  /**
   * Creates the background image layer. The image is rendered over the
   * background color and tracks the cytoscape viewport: a wrapper carries the
   * pan/zoom transform, and an inner box is positioned/sized in model space so
   * the image keeps a fixed position and scale relative to the canvas contents.
   */
  private createBackgroundImageLayer(imageUrl?: string, opacity?: number, sizeValue = 0): void {
    this.detachViewportTracking();
    this.imageWrapper = null;
    this.imageBox = null;

    if (!imageUrl) return;

    const container = this.el.nativeElement;
    this.currentSizeValue = sizeValue;

    // Wrapper: fills the container and carries the viewport transform.
    const wrapper = this.renderer.createElement('div');
    this.renderer.addClass(wrapper, 'background-layer');
    this.renderer.setStyle(wrapper, 'position', 'absolute');
    this.renderer.setStyle(wrapper, 'top', '0');
    this.renderer.setStyle(wrapper, 'left', '0');
    this.renderer.setStyle(wrapper, 'width', '100%');
    this.renderer.setStyle(wrapper, 'height', '100%');
    this.renderer.setStyle(wrapper, 'transform-origin', '0 0');
    this.renderer.setStyle(wrapper, 'pointer-events', 'none');
    this.renderer.setStyle(wrapper, 'z-index', '-1');
    this.renderer.setStyle(wrapper, 'opacity', this.clamp(opacity ?? 1, 0, 1).toString());

    // Inner box: sized/positioned in model space, holds the image itself.
    const box = this.renderer.createElement('div');
    this.renderer.setStyle(box, 'position', 'absolute');
    this.renderer.setStyle(box, 'background-image', `url(${imageUrl})`);
    this.renderer.setStyle(box, 'background-size', '100% 100%');
    this.renderer.setStyle(box, 'background-repeat', 'no-repeat');

    this.renderer.appendChild(wrapper, box);
    this.renderer.appendChild(container, wrapper);
    this.imageWrapper = wrapper;
    this.imageBox = box;

    // Resolve the image's real, actual size, then lay out the box.
    const cached = this.naturalSizeByUrl.get(imageUrl);
    if (cached) {
      this.currentNatural = cached;
    } else {
      this.currentNatural = null;
      const img = new Image();
      img.onload = () => {
        const natural = { w: img.naturalWidth || 1, h: img.naturalHeight || 1 };
        this.naturalSizeByUrl.set(imageUrl, natural);
        // Guard against a stale load after the layer was rebuilt/removed.
        if (this.imageBox !== box) return;
        this.currentNatural = natural;
        this.applyLayout();
        this.updateViewportTransform();
      };
      img.src = imageUrl;
    }

    this.attachViewportTracking();
  }

  /** Attaches (once cytoscape is ready) a listener that keeps the image in sync with pan/zoom. */
  private attachViewportTracking(attemptsLeft = 120): void {
    const cy = this.cyService.cy;
    if (!cy) {
      if (attemptsLeft <= 0) return;
      this.rafId = requestAnimationFrame(() => this.attachViewportTracking(attemptsLeft - 1));
      return;
    }
    this.boundCy = cy;
    this.viewportHandler = () => this.updateViewportTransform();
    cy.on('render pan zoom resize', this.viewportHandler);
    // Now that cytoscape is available, (re)compute the model-space layout and paint.
    this.applyLayout();
    this.updateViewportTransform();
  }

  private detachViewportTracking(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.boundCy && this.viewportHandler) {
      this.boundCy.off('render pan zoom resize', this.viewportHandler);
    }
    this.boundCy = null;
    this.viewportHandler = null;
  }

  /** Positions and sizes the image box in model space around the fixed center point. */
  private applyLayout(): void {
    if (!this.imageBox || !this.currentNatural) return;
    const { rw, rh } = this.computeRenderSize(
      this.currentNatural.w,
      this.currentNatural.h,
      this.currentSizeValue
    );
    const center = this.resolveCenter();
    this.renderer.setStyle(this.imageBox, 'left', `${center.x - rw / 2}px`);
    this.renderer.setStyle(this.imageBox, 'top', `${center.y - rh / 2}px`);
    this.renderer.setStyle(this.imageBox, 'width', `${rw}px`);
    this.renderer.setStyle(this.imageBox, 'height', `${rh}px`);
  }

  /** Applies the current cytoscape pan/zoom to the wrapper so the image tracks the canvas. */
  private updateViewportTransform(): void {
    if (!this.imageWrapper) return;
    const cy = this.boundCy;
    if (!cy) return;
    const pan = cy.pan();
    const zoom = cy.zoom();
    this.renderer.setStyle(
      this.imageWrapper,
      'transform',
      `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`
    );
  }

  /**
   * Computes the render size from the real width W and height H, the slider
   * value V, and the hardcoded magnitude M:
   *   V in [-1, 0):  W / (1 - V * (M - 1))   (down to W/M at V = -1)
   *   V == 0:        W                        (real size)
   *   V in (0, +1]:  W * (1 + V * (M - 1))    (up to W*M at V = +1)
   */
  private computeRenderSize(w: number, h: number, v: number): { rw: number; rh: number } {
    const m = this.IMAGE_SIZE_MAGNITUDE;
    const clampedV = this.clamp(v, -1, 1);
    let factor: number;
    if (clampedV < 0) {
      factor = 1 / (1 - clampedV * (m - 1));
    } else if (clampedV > 0) {
      factor = 1 + clampedV * (m - 1);
    } else {
      factor = 1;
    }
    return { rw: w * factor, rh: h * factor };
  }

  /**
   * Resolves the image's center point in model space. It is set once (at
   * creation / first open) and does not change thereafter:
   *  - blank smap: center of the initial (only) node — same as its bbox center.
   *  - templated/duplicated/generated: center of the bounding box of all elements.
   *  - otherwise (no elements): center of the visible canvas.
   */
  private resolveCenter(): { x: number; y: number } {
    const map = this.mapService.getSelectedMapSignal();
    const stored = map?.styles?.backgroundImageCenter;
    if (stored && typeof stored.x === 'number' && typeof stored.y === 'number') {
      return stored;
    }

    const cy = this.boundCy || this.cyService.cy;
    if (!cy) return { x: 0, y: 0 };

    let center: { x: number; y: number };
    if (cy.nodes().length > 0) {
      const bb = cy.elements().boundingBox();
      center = { x: (bb.x1 + bb.x2) / 2, y: (bb.y1 + bb.y2) / 2 };
    } else {
      const pan = cy.pan();
      const zoom = cy.zoom() || 1;
      center = {
        x: (cy.width() / 2 - pan.x) / zoom,
        y: (cy.height() / 2 - pan.y) / zoom
      };
    }

    this.persistCenter(map, center);
    return center;
  }

  /** Freezes the computed center on the map (once) so it is stable across reloads. */
  private persistCenter(map: GraphMap | undefined, center: { x: number; y: number }): void {
    if (!map?.id) return;
    if (this.centerPersistedForMapId === map.id) return;
    if (!this.mapRoleService.hasCurrentUserPermission('edit', map) &&
        !this.mapRoleService.hasCurrentUserPermission('manage', map)) {
      return;
    }
    this.centerPersistedForMapId = map.id;
    // Mutate in place so the running effect's signal reference is unchanged
    // (no re-render loop), then persist to the backend.
    map.styles = { ...(map.styles || {}), backgroundImageCenter: center };
    this.mapService.update(map).subscribe({ error: () => { /* best-effort */ } });
  }

  /** Creates the color overlay layer */
  private createOverlayLayer(color?: string, intensity?: number): void {
    const container = this.el.nativeElement;

    if (color) {
      const overlayLayer = this.renderer.createElement('div');
      this.renderer.addClass(overlayLayer, 'overlay-layer');
      this.renderer.setStyle(overlayLayer, 'position', 'absolute');
      this.renderer.setStyle(overlayLayer, 'top', '0');
      this.renderer.setStyle(overlayLayer, 'left', '0');
      this.renderer.setStyle(overlayLayer, 'width', '100%');
      this.renderer.setStyle(overlayLayer, 'height', '100%');
      this.renderer.setStyle(overlayLayer, 'pointer-events', 'none');
      this.renderer.setStyle(overlayLayer, 'z-index', '-1'); // Above the background image layer

      const overlayColor = this.getColorWithIntensity(color, intensity ?? 1);
      this.renderer.setStyle(overlayLayer, 'background-color', overlayColor);
      this.renderer.appendChild(container, overlayLayer);
    }
  }

  /** Converts a color to a hex format and adjusts intensity using HSL transformation */
  private getColorWithIntensity(color: string, intensity: number): string {
    try {
      const isDark = this.isDarkMode();

      if (intensity === 0) {
        return isDark ? '#000000' : '#FFFFFF'; // Black for dark mode, white for light mode
      } else {
        // Use HSL transformation with dynamic target luminance
        const targetLuminance = isDark ? 0.0 : 1.0; // Black for dark mode, white for light mode
        return transformColorWithHSL(
          color,
          targetLuminance,
          intensity
        );
      }
    } catch (e) {
      console.error('Invalid color provided:', color, e);
      return this.DEFAULT_COLOR; // Fallback to default color
    }
  }

  /** Updates the CSS variable for label stroke colors */
  private updateBackgroundColorVariable(color?: string, intensity?: number): void {
    const backgroundColor = color ? this.getColorWithIntensity(color, intensity ?? 1) : this.DEFAULT_COLOR;
    document.documentElement.style.setProperty('--smap-background-color', backgroundColor);
  }

  /** Determines if dark mode should be applied based on map and settings */
  private isDarkMode(): boolean {
    const map = this.mapService.getSelectedMapSignal();
    return this.settingsService.isDarkMode(map?.styles?.appearance);
  }

  /** Clamps a value to a given range */
  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }
}
